import { ClientLogger } from 'bernie-client';

import { BadgeAdapter } from 'stores/adapters/bex-api/search/badge-adapter';
import { STORE_EVENTS } from 'stores/events/store-events';

import {
  GalleryViewModel,
  HotelFlags,
  HotelViewModel,
  ImageViewModel,
  MessagingCardViewModel,
  PinnedHotelViewModel,
  PriceViewModel,
  PrimaryCardMessagingModel,
  ReviewsViewModel,
  ShortlistModel,
  TravelAd
} from 'models';
import { ResultType } from 'constants/resultType';
import { ThemeName } from 'constants/theme';

/* tslint:disable:max-file-line-count */

import { CommonPropertyCardFields, FullPropertySearch, PropertyMapMigration, StrikeOutType } from '__generated__/api';
import { MessagingCardProps } from 'components/common/messaging-card';

const propertyType = 'HotelViewModel';
const logger = ClientLogger.getLoggerWithIdentifier('pwa.adapter.bex-api.search-results');

export const mapPropertySearchResults = (query: FullPropertySearch.Query | PropertyMapMigration.Query): HotelViewModel[] => {
  const properties = query.propertySearch.properties;

  if (!properties) {
    return null;
  }

  return (properties as Array<PropertyMapMigration.Properties | FullPropertySearch.Properties>).map(mapProperty);
};

export const mapPropertySearchMessage = (query: FullPropertySearch.Query): MessagingCardProps[] | null =>
  mapMessage(query.propertySearch.summary);

export const mapSearchedDestination = (query: FullPropertySearch.Query): string | null =>
  query.propertySearch.summary.region && query.propertySearch.summary.region.name;

const mapProperty = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): HotelViewModel => {
  const id = getPropertyId(property);
  const priceModel = getPriceModel(property);
  const propertyReviews = getReviewsModel(property.reviews);
  const primaryCardMessagingModel = getPrimaryCardMessagingModel(property);
  const primaryCardMessages = getPrimaryCardMessages(property);
  const propertyFlags = getHotelFlags(property);
  const image = getThumbnail(property);
  const badge = BadgeAdapter.getPropertyListingBadgeModel(property);
  const pinned = getPinnedHotelViewModel(property);

  return {
    hotelName: property.name,
    name: property.name,
    hotelId: id,
    id,
    infositeUrl: property.infositeURL,
    stars: getStarRating(property),
    starRatingIcon: property.starRatingIcon,
    latitude: getCoordinate(property, 'latitude'),
    longitude: getCoordinate(property, 'longitude'),
    type: propertyType,
    resultType: ResultType.HOTEL,
    displayPrice: priceModel.lead,
    neighborhood: getLocationMessaging(property),
    hasAvailableOffer: propertyFlags.hasAvailableOffer,
    freeCancel: propertyFlags.freeCancel,
    vip: propertyFlags.isVip,
    propertyListingBadgeModel: badge,
    price: priceModel,
    reviews: propertyReviews,
    primaryCardMessagingModel,
    primaryCardMessages,
    hotelFlags: propertyFlags,
    thumbnail: image,
    pinnedDetails: pinned,
    vipMessage: property.vipMessaging,
    supportingMessages: property.supportingMessages,
    shortlistModel: getShortList(property),
    travelAd: getTravelAd(property),
    newHotel: propertyFlags.newHotel,
    gallery: getGalleryModel(property.gallery),
    propertySummary: property.highlightedPropertyDetail
  };
};

export const getGalleryModel = (gallery: FullPropertySearch.Gallery[] | PropertyMapMigration.Gallery[]): GalleryViewModel => {
  if (!gallery) {
    return;
  }

  return {
    images: gallery.map(image => ({
      caption: image.description,
      src: image.url
    })),
    thumbnailGalleryEnabled: false // EXPERIMENT = SHOPPING_PWA_THUMBNAIL_GALLERY - 100% control since 2018.
  };
};

// TODO: Change PWA model to string
const getPropertyId = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): number | null =>
  property.id ? Number.parseInt(property.id) : null;

/**
 * Only return the star rating on a valid number
 */
const getStarRating = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): number | null =>
  property.star && property.star > 0 ? property.star : null;

/**
 * Return distance from messaging if available, otherwise returns neighborhood
 */
const getLocationMessaging = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): string | null => {
  if (property.destinationInfo && property.destinationInfo.distanceFromMessaging) {
    return property.destinationInfo.distanceFromMessaging;
  }

  return property.neighborhood ? property.neighborhood.name : null;
};

/**
 * Return coordinates, or null if there are no matching fields
 */
const getCoordinate = (
  property: FullPropertySearch.Properties | PropertyMapMigration.Properties,
  keyName: 'latitude' | 'longitude'
): string | null => {
  const value = property.mapMarker && property.mapMarker.latLong && property.mapMarker.latLong[keyName];

  return value ? value.toString() : null;
};

const adaptPriceMessages = (price: CommonPropertyCardFields.Price | null) =>
  price && price.priceMessages && price.priceMessages.map(message => message.value);

/**
 * Always return a price model but the fields can be null
 */
const getPriceModel = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): PriceViewModel => {
  const loyaltyPrice: CommonPropertyCardFields.Price | null = property.priceAfterLoyaltyPointsApplied;
  const formattedLoyaltyPrice = loyaltyPrice && loyaltyPrice.lead && loyaltyPrice.lead.formatted;
  const loyaltyStrikeOut = loyaltyPrice && loyaltyPrice.strikeOut && loyaltyPrice.strikeOut.formatted;
  const loyaltyStrikeOutType = loyaltyPrice && loyaltyPrice.strikeOutType;
  const price: CommonPropertyCardFields.Price | null = property.price;

  return {
    lead: formattedLoyaltyPrice || (price && price.lead && price.lead.formatted),
    strikeOut: (price && price.strikeOut && price.strikeOut.formatted) || loyaltyStrikeOut,
    strikeOutType: (price && price.strikeOutType) || loyaltyStrikeOutType || StrikeOutType.INVALID,
    priceMessage:
      property.availability && property.availability.available
        ? null
        : {
            text: property.availability && property.availability.text,
            theme: ThemeName[property.availability.theme]
          },
    numberOfNightsAndRooms: price && price.roomNightMessage,
    priceMessages: adaptPriceMessages(price)
  };
};

/**
 * Property reviews are non-null
 */
export const getReviewsModel = (reviews: CommonPropertyCardFields.Reviews): ReviewsViewModel => ({
  overallRating: reviews.score,
  localizedOverallRating: reviews.localizedScore,
  count: reviews.total,
  localizedCount: reviews.localizedTotal,
  superlative: reviews.superlative
});

/**
 * Property Primary messaging model
 */
const getPrimaryCardMessagingModel = (
  property: FullPropertySearch.Properties | PropertyMapMigration.Properties
): PrimaryCardMessagingModel => {
  const offerSummary: FullPropertySearch.OfferSummary | null = property.offerSummary;

  return {
    description: offerSummary && offerSummary.message,
    theme: offerSummary && offerSummary.theme && ThemeName[offerSummary.theme]
  };
};

/**
 * Property Primary messages model, used when sending multiple messages
 */
const getPrimaryCardMessages = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): PrimaryCardMessagingModel[] => {
  const offerSummary: FullPropertySearch.Messages[] | null = property.offerSummary.messages;
  if (offerSummary && offerSummary.length > 0) {
    return offerSummary.map(summary => ({
      description: summary.message,
      theme: ThemeName[summary.theme],
      type: summary.type
    }));
  }

  return null;
};

/**
 * Property availability is non-null
 */
const getHotelFlags = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): HotelFlags => ({
  hasAvailableOffer: property.availability.available,
  isVip: property.vipMessaging != null,
  newHotel: false
});

/**
 * Return null if there is no image, instead of a null url
 */
const getThumbnail = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): ImageViewModel | null => {
  const image: CommonPropertyCardFields.ThumborImage | null = property.thumborImage;
  if (!image) {
    return null;
  }

  return {
    caption: image.description,
    thumbnailSrc: image.url,
    src: image.url,
    fallBackImage: image.url
  };
};

/**
 * Always return a model, but the fields can be null if there is no offerBadge
 */
const getPinnedHotelViewModel = (
  property: FullPropertySearch.Properties | PropertyMapMigration.Properties
): PinnedHotelViewModel | null => {
  if (!property.pinnedDetails) {
    return null;
  }

  return {
    heading: property.pinnedDetails.heading
  };
};

const getShortList = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): ShortlistModel | null => {
  if (!property.shortList || !property.shortList.metaData) {
    return null;
  }

  return {
    favorited: property.shortList.favorited,
    saveUrl: property.shortList.saveUrl,
    removeUrl: property.shortList.removeUrl,
    meta: {
      hotelId: property.shortList.metaData.hotelId,
      chkIn: property.shortList.metaData.chkIn,
      chkOut: property.shortList.metaData.chkOut,
      roomConfiguration: property.shortList.metaData.roomConfiguration
    },
    clickTracking: []
  };
};

export const mapMessageModel = (message: FullPropertySearch._Messages): MessagingCardViewModel | null => {
  const title = message.title;
  const subtitle = message.subtitle;
  const messageAction = message.action;
  const primaryAction = messageAction && messageAction.primary;
  const secondaryAction = messageAction && messageAction.secondary;

  return {
    titleModel: {
      text: title && title.text,
      icon: title && title.icon && title.icon.id,
      theme: title && title.theme && title.theme.toString()
    },
    subtitleModel: {
      text: subtitle && subtitle.text,
      icon: subtitle && subtitle.icon && subtitle.icon.id,
      theme: subtitle && subtitle.theme && subtitle.theme.toString()
    },
    actionModel: {
      primary: {
        href: primaryAction && primaryAction.linkUrl,
        text: primaryAction && primaryAction.text,
        rfrr: primaryAction && primaryAction.referrerId,
        actionDetails: {
          name: primaryAction && primaryAction.actionDetails && primaryAction.actionDetails.name,
          details: primaryAction && primaryAction.actionDetails && primaryAction.actionDetails.details
        }
      },
      secondary: {
        href: secondaryAction && secondaryAction.linkUrl,
        text: secondaryAction && secondaryAction.text,
        rfrr: secondaryAction && secondaryAction.referrerId,
        actionDetails: {
          name: secondaryAction && secondaryAction.actionDetails && secondaryAction.actionDetails.name,
          details: secondaryAction && secondaryAction.actionDetails && secondaryAction.actionDetails.details
        }
      }
    }
  };
};

const mapMessage = (summary: FullPropertySearch.Summary): MessagingCardProps[] | null => {
  const renderMessages = summary && summary.messages && summary.messages.length > 0;
  const messages =
    renderMessages &&
    summary.messages.map(message => ({
      index: message.index,
      model: mapMessageModel(message)
    }));

  return messages;
};

export const mapOmnitureData = (analytics: any): any | null => {
  if (Array.isArray(analytics) && analytics.length > 0 && analytics[0]) {
    return analytics[0];
  } else {
    logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_OMNITURE_PARSE_ERROR, 'Failed to parse omniture data');
  }

  return null;
};

const getTravelAd = (property: FullPropertySearch.Properties | PropertyMapMigration.Properties): TravelAd | null => {
  const sponsoredListing = property.sponsoredListing;
  if (sponsoredListing) {
    return {
      adId: sponsoredListing.id,
      clickRedirectTrackingUrl: sponsoredListing.clickRedirectTrackingUrl,
      clickTrackingUrl: sponsoredListing.clickTrackingUrl,
      details: sponsoredListing.details,
      detailsHeadline: sponsoredListing.detailsHeadline,
      hotelImage: sponsoredListing.hotelImage,
      impressionTrackingUrl: sponsoredListing.impressionTrackingUrl,
      carouselImpressionTrackingUrl: null,
      trackingData: sponsoredListing.trackingData,
      brand: sponsoredListing.brand,
      clickRedirectLinkoffTrackingUrl: sponsoredListing.clickRedirectLinkoffTrackingUrl,
      logo: sponsoredListing.logo,
      lotagline: sponsoredListing.lotagline,
      rating: sponsoredListing.rating,
      slots: sponsoredListing.slots,
      vanityurl: sponsoredListing.vanityUrl,
      rank: sponsoredListing.rank,
      position: sponsoredListing.position,
      candidateHmGuid: '',
      beaconIssued: ''
    };
  }

  return null;
};
